import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { List } from 'immutable';
import FiberManualRecordOutlinedIcon from '@mui/icons-material/FiberManualRecordOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import WifiIcon from '@mui/icons-material/Wifi';

import { FilterChip } from 'src/component/ds/ui/filterChip';
import { Skeleton } from 'src/component/ds/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/component/ds/ui/tooltip';
import { cn } from 'src/component/ds/utils';
import { useTranslation } from 'src/i18n';
import { getCriticalityLabels } from 'src/module/authentication/selector';
import { getDeviceListError, getDeviceListFilterCounts } from 'src/module/deviceList/selector';
import { toggleSelectedAttribute } from 'src/module/filter/action';
import { getSelectedAttributes } from 'src/module/filter/selector';
import {
  SEVERITY_ICON,
  SEVERITY_STYLE,
  toSeverityCounts
} from 'src/screen/v2/Protected/DashboardScreen/AlarmsToTriage/severity';

interface ChipSpec {
  attribute: string;
  label: string;
  icon: React.ReactNode;
  iconOnly?: boolean;
}

const SKELETON_WIDTHS = ['ds-w-[103px]', 'ds-w-[111px]', 'ds-w-[122px]', 'ds-w-[96px]', 'ds-w-[59px]', 'ds-w-[59px]', 'ds-w-[59px]', 'ds-w-[59px]'];

const STATUS_CHIPS: (Omit<ChipSpec, 'label'> & { labelKey: string })[] = [
  { attribute: 'online', labelKey: 'deviceList.online', icon: <WifiIcon fontSize='inherit' className='ds-text-online' /> },
  { attribute: 'offline', labelKey: 'deviceList.offline', icon: <FiberManualRecordOutlinedIcon fontSize='inherit' /> },
  { attribute: 'forcedOn', labelKey: 'deviceList.forcedOn', icon: <PowerSettingsNewIcon fontSize='inherit' className='ds-text-forced-on' /> },
  { attribute: 'loto', labelKey: 'deviceList.filters.loto', icon: <LockOutlinedIcon fontSize='inherit' /> },
  { attribute: 'alarming', labelKey: 'deviceList.filters.alarming', icon: <NotificationsActiveOutlinedIcon fontSize='inherit' />, iconOnly: true },
];

export const deviceFilterChipKeys = (labels: List<string>): string[] => [
  ...STATUS_CHIPS.map(({ attribute }) => attribute),
  ...toSeverityCounts({}, labels).map(({ criticality }) => `alarm:${criticality}`)
];

export default function DeviceFilterChips ({ className }: { className?: string }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const labels = useSelector(getCriticalityLabels);
  const applied = useSelector(getSelectedAttributes);
  const counts = useSelector(getDeviceListFilterCounts);
  const error = useSelector(getDeviceListError);

  const chips: ChipSpec[] = useMemo(() => [
    ...STATUS_CHIPS.map(({ labelKey, ...chip }) => ({ ...chip, label: t(labelKey) })),
    ...toSeverityCounts({}, labels).map(({ severity, label, criticality }) => {
      const Icon = SEVERITY_ICON[severity];
      return {
        attribute: `alarm:${criticality}`,
        label,
        icon: <Icon fontSize='inherit' className={SEVERITY_STYLE[severity].icon} />,
        iconOnly: true
      };
    })
  ], [t, labels]);

  if (!counts && !error) {
    return (
      <div className={cn('ds-flex ds-flex-wrap ds-gap-2', className)}>
        {SKELETON_WIDTHS.map((width, i) => (
          <Skeleton key={i} className={cn('ds-h-5.5 ds-rounded-full', width)} data-testid='device-filter-chip-skeleton' />
        ))}
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div role='group' aria-label={t('deviceList.filters.label')} className={cn('ds-flex ds-flex-wrap ds-gap-2', className)}>
        {chips.map(({ attribute, label, icon, iconOnly }) => {
          const chip = (
            <FilterChip
              key={attribute}
              label={label}
              icon={icon}
              hideLabel={iconOnly}
              count={counts?.[attribute]}
              applied={applied.includes(attribute)}
              onClick={() => dispatch(toggleSelectedAttribute(attribute))}
              testId={`device-filter-chip-${attribute}`}
            />
          );
          if (!iconOnly) {
            return chip;
          }
          return (
            <Tooltip key={attribute}>
              <TooltipTrigger asChild>{chip}</TooltipTrigger>
              <TooltipContent>{label}</TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
